/**
 * Cursor-on-Target (CoT) protobuf utilities for the ForceTracker frontend.
 *
 * Provides SIDC <-> CoT type conversion and functions to build/parse
 * TAK Protocol CoT protobuf messages. Mirrors the backend Python
 * implementation in ft-backend/app/utils/cot_utils.py.
 *
 * SIDC mapping supports both the MIL-STD-2525 legacy 15-character format
 * and the APP-6D 20-character numeric format.
 */

import { MapEntity } from "../components/map/mapTypes.d";
import {
    encodeTakMessage,
    decodeTakMessage,
    CotEventPayload,
} from "../proto/takProto";
import { formatDtg } from "./parseTimestamp";

// ---------------------------------------------------------------------------
// SIDC <-> CoT type conversion mappings
// ---------------------------------------------------------------------------

/** Legacy SIDC position 1 (affiliation) -> CoT affiliation letter */
const SIDC_AFFIL_TO_COT: Record<string, string> = {
    F: "f", // friendly
    A: "f", // assumed friendly
    H: "h", // hostile
    S: "h", // suspect
    N: "n", // neutral
    L: "n", // exercise neutral
    U: "u", // unknown
    P: "u", // pending
};

/** CoT affiliation letter -> SIDC affiliation char */
const COT_AFFIL_TO_SIDC: Record<string, string> = {
    f: "F",
    h: "H",
    n: "N",
    u: "U",
};

/** Legacy SIDC position 2 (battle dimension) -> CoT dimension char */
const SIDC_DIM_TO_COT: Record<string, string> = {
    P: "P", // space
    A: "A", // air
    G: "G", // ground
    S: "S", // sea surface
    U: "U", // sea subsurface
    F: "G", // SOF (treated as ground)
};

/** CoT dimension char -> SIDC battle dimension char */
const COT_DIM_TO_SIDC: Record<string, string> = {
    P: "P",
    A: "A",
    G: "G",
    S: "S",
    U: "U",
};

/** APP-6D numeric identity (position 3) -> CoT affiliation letter */
const APP6D_IDENTITY_TO_COT: Record<string, string> = {
    "0": "u", // assumed unknown
    "1": "u", // unknown
    "2": "f", // assumed friend
    "3": "f", // friend
    "4": "n", // neutral
    "5": "h", // suspect / joker
    "6": "h", // hostile / faker
};

/** APP-6D numeric symbol set (positions 4-5) -> CoT dimension char */
const APP6D_SYMBOLSET_TO_COT: Record<string, string> = {
    "10": "G", // land unit
    "15": "G", // land equipment
    "20": "G", // land installation
    "30": "S", // sea surface
    "35": "U", // sea subsurface
    "50": "A", // air
    "51": "A", // air missile
};

// ---------------------------------------------------------------------------
// SIDC <-> CoT type conversion functions
// ---------------------------------------------------------------------------

/**
 * CoT type for a user-drawn graphic ("user drawing, freeform"), the type ATAK
 * uses for hand-drawn shapes.
 *
 * Graphics deliberately do not use an `a-<affiliation>-<dimension>` atom type: an
 * atom would tell a naive TAK consumer to draw a single unit icon at the
 * graphic's centroid. The authoritative symbology is always the full APP-6D SIDC
 * carried in `xmlDetail`.
 */
export const COT_TYPE_DRAWN_GRAPHIC = "u-d-f";

/** APP-6D symbol set for control measures (tactical graphics), positions 4-5. */
const APP6D_SYMBOLSET_CONTROL_MEASURE = "25";

/**
 * Convert an SIDC code to a CoT type string.
 *
 * Supports both the legacy 15-char letter-based format and the
 * APP-6D 20-char numeric format.
 *
 * @example
 * sidcToCotType("SFGPUCI----B") // "a-f-G-U-C-I"
 * sidcToCotType("10031000001211000000") // "a-f-G"
 */
export function sidcToCotType(sidc: string): string {
    if (!sidc) {
        return "a-u-G";
    }

    // APP-6D numeric format starts with a digit
    if (sidc[0] >= "0" && sidc[0] <= "9") {
        /*
         * Symbol set 25 (control measures) is a multipoint tactical graphic, not
         * a point entity, so it maps to the drawn-graphic type regardless of
         * identity. Without this branch an axis-of-advance SIDC falls through to
         * the "G" default and claims to be a friendly ground *unit* sitting at
         * the graphic's centroid.
         */
        if (
            sidc.length > 5 &&
            sidc.substring(4, 6) === APP6D_SYMBOLSET_CONTROL_MEASURE
        ) {
            return COT_TYPE_DRAWN_GRAPHIC;
        }

        const affil =
            sidc.length > 3
                ? APP6D_IDENTITY_TO_COT[sidc[3]] ?? "u"
                : "u";
        const dim =
            sidc.length > 5
                ? APP6D_SYMBOLSET_TO_COT[sidc.substring(4, 6)] ?? "G"
                : "G";
        return `a-${affil}-${dim}`;
    }

    const affil =
        sidc.length > 1 ? SIDC_AFFIL_TO_COT[sidc[1]] ?? "u" : "u";
    const dim =
        sidc.length > 2 ? SIDC_DIM_TO_COT[sidc[2]] ?? "G" : "G";

    /*
     * Function ID occupies positions 4-9 (6 chars); positions 10+ are
     * modifiers / country / order-of-battle, which are not part of the CoT type.
     * Bounding the slice keeps this identical to `sidc_to_cot_type` in
     * `ft-backend/app/utils/cot_utils.py` — without the upper bound, a 12-char
     * SIDC like `SFGPUCI----B` leaked its trailing modifier into the type as
     * `a-f-G-U-C-I---------B`.
     */
    let funcChars = "";
    if (sidc.length > 4) {
        funcChars = sidc.substring(4, 10);
        // Strip trailing dashes (filler)
        funcChars = funcChars.replace(/-+$/, "");
    }

    const parts = ["a", affil, dim];
    if (funcChars) {
        parts.push(...funcChars.split(""));
    }

    return parts.join("-");
}

/**
 * Convert a CoT type string back to a legacy 15-char SIDC code.
 *
 * @example
 * cotTypeToSidc("a-f-G-U-C-I") // "SFGPUCI--------"
 */
export function cotTypeToSidc(cotType: string): string {
    const parts = cotType.split("-");

    const codingScheme = "S";
    const affil =
        parts.length > 1 ? COT_AFFIL_TO_SIDC[parts[1]] ?? "U" : "U";
    const dim =
        parts.length > 2 ? COT_DIM_TO_SIDC[parts[2]] ?? "G" : "G";
    const status = "P";

    // Function code characters start at parts[3:]
    const funcChars = parts.length > 3 ? parts.slice(3).join("") : "";

    // Pad function codes to fill remaining 11 positions (pos 4-14)
    const funcPadded = funcChars.padEnd(11, "-");

    const sidc = codingScheme + affil + dim + status + funcPadded;
    return sidc.substring(0, 15);
}

// ---------------------------------------------------------------------------
// CoT protobuf encoding/decoding
// ---------------------------------------------------------------------------

/** Result of parsing an incoming CoT protobuf message. */
export interface ParsedCotMessage {
    uid: string;
    type: string;
    how: string;
    lat: number;
    lon: number;
    hae: number;
    ce: number;
    le: number;
    sendTime: number;
    startTime: number;
    staleTime: number;
    callsign: string;
    groupName: string;
    xmlDetail: string;
    speed: number;
    course: number;
    sidc: string;
    /**
     * Text modifiers extracted from the `<ft>` root element of `xmlDetail`.
     * Reserved attributes (`sidc`, `is_deleted`) are stripped. Always an
     * object — empty `{}` when no modifiers were present or the XML was
     * malformed.
     */
    textModifiers: Record<string, string>;
}

/**
 * xmlDetail attributes with dedicated semantics, which must never be treated as
 * free-form text modifiers.
 *
 * `points`, `unit_id` and `marker_id` belong to tactical graphics: the first
 * carries the geometry, the other two the parent the graphic is attached to. If
 * they were not reserved, a coordinate list would land in a marker's
 * `markerProperties` and milsymbol would try to render it as a symbol label.
 */
export const RESERVED_FT_ATTRS = new Set([
    "sidc",
    "is_deleted",
    "points",
    "unit_id",
    "marker_id",
]);

/**
 * Modifier keys that are *derived* at render time and must never cross the
 * wire in either direction.
 *
 * `dtg` is computed from the message timestamp and `uniqueDesignation` from the
 * entity's `name` — each is injected into the milsymbol options at the call
 * site that draws the symbol, so a copy on the wire is at best a duplicate and
 * at worst a stale one that shadows the real value. The backend enforces the
 * same set on write (`DERIVED_MODIFIER_KEYS` in `app/utils/cot_utils.py`), as
 * does Android (`CotMapper.RESERVED_FT_ATTRS`).
 *
 * Kept separate from {@link RESERVED_FT_ATTRS} rather than added to it: those
 * are protocol attributes a caller may legitimately opt back in to via
 * `allowReserved` (the graphics builder owns `points`, `unit_id`, `marker_id`).
 * A derived key has no such caller and must not be allowlistable.
 */
export const DERIVED_MODIFIER_KEYS = new Set(["dtg", "uniqueDesignation"]);

/**
 * Escape a string for safe inclusion as an XML attribute value.
 * Handles `&`, `<`, `>`, and `"` — the four characters that would
 * otherwise break the attribute or surrounding markup.
 */
function escapeXmlAttr(v: string): string {
    return v
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

/**
 * Build the `<ft .../>` xmlDetail string with `sidc` first and any
 * text modifier attributes sorted alphabetically for deterministic
 * output. Reserved keys (`sidc`, `is_deleted`) and the derived overlays
 * (`dtg`, `uniqueDesignation` — see {@link DERIVED_MODIFIER_KEYS}) are
 * filtered out from `modifiers`; `null`/`undefined` values are skipped.
 */
function buildFtXmlDetail(
    sidc: string,
    modifiers?: Record<string, unknown>,
    allowReserved: Set<string> = new Set()
): string {
    const attrs = [`sidc="${escapeXmlAttr(sidc)}"`];
    if (modifiers) {
        const keys = Object.keys(modifiers)
            .filter(
                (k) =>
                    !DERIVED_MODIFIER_KEYS.has(k) &&
                    (!RESERVED_FT_ATTRS.has(k) || allowReserved.has(k))
            )
            .sort();
        for (const k of keys) {
            const v = modifiers[k];
            if (v === null || v === undefined) continue;
            attrs.push(`${k}="${escapeXmlAttr(String(v))}"`);
        }
    }
    return `<ft ${attrs.join(" ")} />`;
}

/**
 * Build a CoT protobuf binary from a position update.
 *
 * Creates a TakMessage with a CotEvent populated from the provided
 * unit parameters. Used for publishing position updates over MQTT.
 *
 * Text modifiers (commonIdentifier, additionalInformation, …) are
 * serialized as attributes on the `<ft>` root element of `xmlDetail`,
 * sorted alphabetically for deterministic output. Reserved keys
 * (`sidc`, `is_deleted`) and the derived overlays (`dtg`,
 * `uniqueDesignation`) are dropped before serialization — the entity's
 * name travels in `callsign`, not as a modifier.
 *
 * @param unitId        - Unique identifier for the unit.
 * @param sidc          - MIL-STD-2525 SIDC code.
 * @param lat           - Latitude in decimal degrees.
 * @param lng           - Longitude in decimal degrees.
 * @param callsign      - Optional display name / callsign.
 * @param textModifiers - Optional map of marker text-modifier attrs.
 * @returns Uint8Array of serialized protobuf bytes.
 */
export function buildPositionCot(
    unitId: string,
    sidc: string,
    lat: number,
    lng: number,
    callsign?: string,
    textModifiers?: Record<string, unknown>
): Uint8Array {
    const nowMs = Date.now();
    const staleMs = nowMs + 120000; // Position stales after 120s

    const cotEvent: CotEventPayload = {
        type: sidcToCotType(sidc),
        uid: unitId,
        how: "h-e", // human-entered
        sendTime: nowMs,
        startTime: nowMs,
        staleTime: staleMs,
        lat,
        lon: lng,
        hae: 0.0,
        ce: 9999999.0,
        le: 9999999.0,
        detail: {
            xmlDetail: buildFtXmlDetail(sidc, textModifiers),
            contact: callsign ? { callsign } : undefined,
            takv: {
                platform: "ForceTracker-Web",
                version: "1.0.0",
                os: "browser",
                device: "web-client",
            },
        },
    };

    return encodeTakMessage({ cotEvent });
}

/**
 * Build a CoT protobuf binary for a marker deletion tombstone.
 *
 * Mirrors the backend helper `deleted_marker_to_cot_bytes` in
 * `ft-backend/app/utils/cot_utils.py`. The payload carries only the
 * identity of the deleted marker — all position fields are zeroed and
 * `xmlDetail` includes a sentinel `<ft is_deleted="true" />` tag so
 * subscribers can distinguish a tombstone from an ordinary update.
 *
 * Used by the client-initiated marker-delete flow: the web UI publishes
 * this payload directly to `{eventId}/{faction_id ?? 'shared'}/markers/deleted`.
 * The backend `MarkersHandler` consumes it and persists the soft-delete
 * to the DB, while other clients receive the same broadcast via their
 * MQTT subscription and remove the marker from their local Redux store
 * through `MarkersManager`'s existing `"deleted"` branch.
 *
 * @param markerId - The UUID of the marker being deleted.
 * @returns Uint8Array of serialized protobuf bytes.
 */
export function buildDeletedMarkerCot(markerId: string): Uint8Array {
    const nowMs = Date.now();

    const cotEvent: CotEventPayload = {
        type: "a-u-G", // unknown/generic ground; identity-only tombstone
        uid: markerId,
        how: "h-e", // human-entered
        sendTime: nowMs,
        startTime: nowMs,
        staleTime: nowMs, // stale immediately — TAK clients remove at once
        lat: 0,
        lon: 0,
        hae: 0,
        ce: 9999999.0,
        le: 9999999.0,
        detail: {
            xmlDetail: '<ft is_deleted="true" />',
        },
    };

    return encodeTakMessage({ cotEvent });
}

// ---------------------------------------------------------------------------
// Tactical graphics (multipoint control measures)
// ---------------------------------------------------------------------------

/** Far-future stale time (2099-01-01Z) — graphics persist until deleted. */
const GRAPHIC_STALE_MS = 4070908800000;

/** Decimal places for graphic coordinates: ~1 cm, far finer than hand drawing. */
const POINT_PRECISION = 7;

/** Symbol parameters, as ground distances in metres. */
export type GraphicParams = Record<string, number>;

/** A tactical graphic decoded from a CoT payload. */
export interface ParsedGraphicCot {
    id: string;
    sidc: string;
    /** Centreline vertices as [lng, lat], tail → tip. */
    points: number[][];
    params: GraphicParams;
    /** Parent unit, or null. Mutually exclusive with `markerId`. */
    unitId: string | null;
    /** Parent marker, or null. Mutually exclusive with `unitId`. */
    markerId: string | null;
    name: string | null;
    factionId: string | null;
}

/**
 * Format graphic geometry for the `points` xmlDetail attribute.
 *
 * Emits space-separated `lng,lat` pairs — **GeoJSON axis order, not CoT's usual
 * lat,lon**. This matches the `graphics.points` column, the renderer's
 * control-point string and the clients' in-memory shape, so coordinates are
 * never swapped anywhere along the path. Must stay byte-identical to
 * `_format_points` in `ft-backend/app/utils/cot_utils.py`.
 */
export function formatGraphicPoints(points: number[][]): string {
    return points
        .map(([lng, lat]) => {
            const trim = (n: number) =>
                n.toFixed(POINT_PRECISION).replace(/0+$/, "").replace(/\.$/, "") || "0";
            return `${trim(lng)},${trim(lat)}`;
        })
        .join(" ");
}

/**
 * Parse a `points` attribute back into `[[lng, lat], …]`.
 *
 * Returns null when the value is absent or malformed. A graphic missing a vertex
 * is invalid rather than degraded, so callers drop the message instead of
 * substituting a partial shape.
 */
export function parseGraphicPoints(raw: string): number[][] | null {
    if (!raw) return null;
    const points: number[][] = [];
    for (const token of raw.trim().split(/\s+/)) {
        const parts = token.split(",");
        if (parts.length !== 2) return null;
        const lng = Number(parts[0]);
        const lat = Number(parts[1]);
        if (!isFinite(lng) || !isFinite(lat)) return null;
        points.push([lng, lat]);
    }
    return points.length > 0 ? points : null;
}

/** Arithmetic mean of a graphic's vertices, as `[lat, lng]`. */
function graphicCentroid(points: number[][]): [number, number] {
    if (points.length === 0) return [0, 0];
    const lng = points.reduce((sum, p) => sum + p[0], 0) / points.length;
    const lat = points.reduce((sum, p) => sum + p[1], 0) / points.length;
    return [lat, lng];
}

/**
 * Build a CoT protobuf binary for a tactical graphic.
 *
 * Mirrors `graphic_to_cot_bytes` in `ft-backend/app/utils/cot_utils.py`.
 * Geometry rides in `xmlDetail` because the CoT `Detail` proto message has no
 * repeated field and no `link` field, and that schema is upstream TAK which must
 * not be modified.
 *
 * `lat`/`lon` carry the geometry's centroid so a consumer that plots the point
 * without reading the geometry does not place the graphic at 0,0 — but the
 * accuracy fields are set to "unknown" to mark it as advisory, not a real fix.
 */
export function buildGraphicCot(graphic: {
    id: string;
    sidc: string;
    points: number[][];
    params?: GraphicParams;
    unitId?: string | null;
    markerId?: string | null;
    name?: string | null;
    factionId?: string | null;
}): Uint8Array {
    const nowMs = Date.now();
    const [lat, lon] = graphicCentroid(graphic.points);

    const attrs: Record<string, unknown> = { ...(graphic.params ?? {}) };
    attrs.points = formatGraphicPoints(graphic.points);
    // At most one parent is ever set, so both can be offered unconditionally.
    if (graphic.unitId) attrs.unit_id = graphic.unitId;
    if (graphic.markerId) attrs.marker_id = graphic.markerId;

    const cotEvent: CotEventPayload = {
        type: COT_TYPE_DRAWN_GRAPHIC,
        uid: graphic.id,
        how: "h-e",
        sendTime: nowMs,
        startTime: nowMs,
        staleTime: GRAPHIC_STALE_MS,
        lat,
        lon,
        hae: 0,
        // The centroid is not a measured fix.
        ce: 9999999.0,
        le: 9999999.0,
        detail: {
            xmlDetail: buildFtXmlDetail(
                graphic.sidc,
                attrs,
                new Set(["points", "unit_id", "marker_id"])
            ),
            contact: graphic.name ? { callsign: graphic.name } : undefined,
            group: { name: graphic.factionId ?? "shared" },
        },
    };

    return encodeTakMessage({ cotEvent });
}

/**
 * Build a CoT protobuf binary for a graphic deletion tombstone.
 *
 * Mirrors `deleted_graphic_to_cot_bytes` in the backend.
 */
export function buildDeletedGraphicCot(graphicId: string): Uint8Array {
    const nowMs = Date.now();

    const cotEvent: CotEventPayload = {
        type: COT_TYPE_DRAWN_GRAPHIC,
        uid: graphicId,
        how: "h-e",
        sendTime: nowMs,
        startTime: nowMs,
        staleTime: nowMs, // stale immediately
        lat: 0,
        lon: 0,
        hae: 0,
        ce: 9999999.0,
        le: 9999999.0,
        detail: {
            xmlDetail: '<ft is_deleted="true" />',
        },
    };

    return encodeTakMessage({ cotEvent });
}

/**
 * Decode a tactical graphic from a CoT payload.
 *
 * Returns null unless the payload carries both a `sidc` and parseable geometry.
 * Deliberately does **not** fall back to `cotTypeToSidc`: `u-d-f` says only "some
 * drawing", so a graphic without its SIDC cannot be rendered, and one without
 * geometry is invalid rather than degraded.
 */
export function parseGraphicCot(
    data: Uint8Array | Buffer
): ParsedGraphicCot | null {
    const parsed = parseCotMessage(data);
    if (!parsed || !parsed.uid) return null;

    const sidcMatch = parsed.xmlDetail.match(/sidc="([^"]+)"/);
    if (!sidcMatch) return null;

    const pointsMatch = parsed.xmlDetail.match(/points="([^"]*)"/);
    const points = pointsMatch ? parseGraphicPoints(pointsMatch[1]) : null;
    if (!points) return null;

    const unitIdMatch = parsed.xmlDetail.match(/unit_id="([^"]+)"/);
    const markerIdMatch = parsed.xmlDetail.match(/marker_id="([^"]+)"/);

    // `textModifiers` already has the reserved attributes stripped, so whatever
    // remains is the symbol params — as strings, since XML attributes always are.
    const params: GraphicParams = {};
    for (const [key, value] of Object.entries(parsed.textModifiers)) {
        const num = Number(value);
        if (isFinite(num)) params[key] = num;
    }

    return {
        id: parsed.uid,
        sidc: sidcMatch[1],
        points,
        params,
        unitId: unitIdMatch ? unitIdMatch[1] : null,
        markerId: markerIdMatch ? markerIdMatch[1] : null,
        name: parsed.callsign || null,
        factionId:
            parsed.groupName && parsed.groupName !== "shared"
                ? parsed.groupName
                : null,
    };
}

/**
 * Parse a CoT protobuf binary message into a structured object.
 *
 * @param data - Uint8Array or Buffer containing TakMessage protobuf bytes.
 * @returns Parsed message fields, or null if decoding fails.
 */
export function parseCotMessage(
    data: Uint8Array | Buffer
): ParsedCotMessage | null {
    try {
        const decoded = decodeTakMessage(data);

        if (!decoded.cotEvent) {
            return null;
        }

        const evt = decoded.cotEvent;
        const detail = evt.detail;

        // Extract SIDC from xmlDetail if present, otherwise derive from CoT type
        let sidc = "";
        if (detail?.xmlDetail) {
            const sidcMatch = detail.xmlDetail.match(/sidc="([^"]+)"/);
            if (sidcMatch) {
                sidc = sidcMatch[1];
            }
        }
        if (!sidc) {
            sidc = cotTypeToSidc(evt.type);
        }

        // Parse all attributes of the `<ft>` root element from xmlDetail
        // and treat anything other than the reserved keys as text
        // modifiers. Malformed XML is tolerated — we just yield `{}`.
        let textModifiers: Record<string, string> = {};
        if (detail?.xmlDetail) {
            try {
                const doc = new DOMParser().parseFromString(
                    detail.xmlDetail,
                    "application/xml"
                );
                const root = doc.documentElement;
                if (root && !root.querySelector("parsererror")) {
                    for (let i = 0; i < root.attributes.length; i++) {
                        const attr = root.attributes[i];
                        if (
                            !RESERVED_FT_ATTRS.has(attr.name) &&
                            !DERIVED_MODIFIER_KEYS.has(attr.name)
                        ) {
                            textModifiers[attr.name] = attr.value;
                        }
                    }
                }
            } catch {
                textModifiers = {};
            }
        }

        return {
            uid: evt.uid,
            type: evt.type,
            how: evt.how,
            lat: evt.lat,
            lon: evt.lon,
            hae: evt.hae,
            ce: evt.ce,
            le: evt.le,
            sendTime: evt.sendTime,
            startTime: evt.startTime,
            staleTime: evt.staleTime,
            callsign: detail?.contact?.callsign ?? "",
            groupName: detail?.group?.name ?? "",
            xmlDetail: detail?.xmlDetail ?? "",
            speed: detail?.track?.speed ?? 0,
            course: detail?.track?.course ?? 0,
            sidc,
            textModifiers,
        };
    } catch (e) {
        console.error("Failed to decode CoT protobuf message:", e);
        return null;
    }
}

// ---------------------------------------------------------------------------
// MapEntity conversion helpers
// ---------------------------------------------------------------------------

/**
 * Convert a parsed CoT message to a MapEntity for the Redux map slice.
 *
 * Returns null if the parsed message has no valid position (lat=0, lon=0)
 * or if the parsed input is null.
 *
 * @param parsed - Output from parseCotMessage().
 * @returns MapEntity ready for dispatch, or null.
 */
export function cotToMapEntity(
    parsed: ParsedCotMessage | null
): MapEntity | null {
    if (!parsed) {
        return null;
    }

    // Skip messages with no position data (e.g. ORBAT lifecycle events)
    if (parsed.lat === 0 && parsed.lon === 0) {
        return null;
    }

    // Prefer sendTime (when this position was sent); fall back to startTime.
    // Both are epoch milliseconds in the CoT protobuf schema.
    const rawTs =
        Number.isFinite(parsed.sendTime) && parsed.sendTime > 0
            ? parsed.sendTime
            : Number.isFinite(parsed.startTime) && parsed.startTime > 0
                ? parsed.startTime
                : null;

    // Derive faction scope from CoT `detail.group.name`. The backend sets
    // it to the faction UUID for faction-scoped markers, or the literal
    // string `"shared"` for event-wide markers. A missing groupName is
    // treated as shared (null) for safety.
    const groupName = parsed.groupName ?? "";
    const factionId =
        groupName && groupName !== "shared" ? groupName : null;

    // Surface any text modifiers parsed from the `<ft>` xmlDetail
    // attributes, plus a `dtg` overlay derived from this message's
    // timestamp. The store MERGES markerProperties on update, so omitting
    // `dtg` here left the stale REST-loaded value in place until a full
    // refresh — a live position update must refresh the on-symbol time.
    // Bare position updates carry no text modifiers but still update dtg.
    const hasModifiers = Object.keys(parsed.textModifiers).length > 0;
    const markerProperties =
        hasModifiers || rawTs != null
            ? {
                ...parsed.textModifiers,
                ...(rawTs != null ? { dtg: formatDtg(rawTs) } : {}),
            }
            : undefined;

    return {
        id: parsed.uid,
        latLng: { lat: parsed.lat, lng: parsed.lon },
        code: parsed.sidc,
        own: false,
        // The CoT callsign IS the entity name (the backend sets it from
        // `name`); surface it as such so the map can derive the symbol label
        // from it. `undefined` rather than `""` so the store's merge does not
        // blank an already-known name on a callsign-less position publish.
        name: parsed.callsign || undefined,
        markerProperties,
        lastUpdateMs: rawTs,
        faction_id: factionId,
    };
}

/**
 * Serialize a MapEntity into CoT protobuf bytes for MQTT publishing.
 *
 * @param entity    - The map entity to serialize.
 * @param factionId - Faction ID (used in topic routing, not in payload).
 * @returns Uint8Array of serialized protobuf bytes.
 */
export function mapEntityToCotBytes(
    entity: MapEntity,
    factionId: string
): Uint8Array {
    // The callsign comes from the entity's name, not from a text modifier:
    // `uniqueDesignation` is a label derived at render time and is stripped
    // below, so reading the callsign from it would send an empty callsign.
    const callsign = entity.name || undefined;
    // `dtg` and `uniqueDesignation` are display overlays derived from
    // position_time and name respectively; neither is a real text modifier
    // and neither may be wire-encoded — the backend strips them on write too
    // (DERIVED_MODIFIER_KEYS), but doing it here keeps the contract obvious at
    // the call site.
    let textModifiers: Record<string, unknown> | undefined;
    if (entity.markerProperties) {
        const {
            dtg: _ignoredDtg,
            uniqueDesignation: _ignoredName,
            ...rest
        } = entity.markerProperties as Record<string, unknown>;
        textModifiers = rest;
    }
    return buildPositionCot(
        entity.id,
        entity.code,
        entity.latLng.lat,
        entity.latLng.lng,
        callsign,
        textModifiers
    );
}
